import { WcpTheme } from '@workday/wcp-cargo-react';

/**
 * The shape stored in localStorage and used throughout ThemeWrapper.
 * `setBy` is client-only — it is never persisted to the backend.
 * Its value is inferred from the presence/absence of `devPlatTheme` in the backend response:
 *   absent/empty → 'DEFAULT' (forced dark launch default)
 *   present      → 'USER'   (user explicitly set it; only way the key gets written)
 */
export interface ThemePreference {
  theme: WcpTheme;
  setBy: 'USER' | 'DEFAULT';
}

export const THEME_CACHE_KEY = 'dev-plat-theme';

/**
 * Returns the default ThemePreference for users without a stored or backend preference.
 * DARK/DEFAULT is the forced default during the launch window.
 */
export function getDefaultThemePreference(): ThemePreference {
  return { theme: 'DARK', setBy: 'DEFAULT' };
}

/**
 * Reads ThemePreference from localStorage (the JSON format written by writeThemePreference).
 * Returns null if the key is absent, invalid, or unrecognisable.
 */
export function readThemePreference(): ThemePreference | null {
  const raw = localStorage.getItem(THEME_CACHE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      (parsed.theme === 'DARK' || parsed.theme === 'LIGHT' || parsed.theme === 'SYSTEM') &&
      (parsed.setBy === 'USER' || parsed.setBy === 'DEFAULT')
    ) {
      return parsed as ThemePreference;
    }
  } catch {
    // Not valid JSON — treat as absent
  }

  return null;
}

/** Persists a ThemePreference to localStorage as JSON. */
export function writeThemePreference(pref: ThemePreference): void {
  localStorage.setItem(THEME_CACHE_KEY, JSON.stringify(pref));
}

/**
 * Applies the stored (or default) theme to the DOM before React hydration to prevent FOUC.
 * Called from the HTML entry point before the React app mounts.
 */
export function applyStoredThemeToDOM(): void {
  const pref = readThemePreference() ?? getDefaultThemePreference();
  const isSystemDark = pref.theme === 'SYSTEM' && window.matchMedia('(prefers-color-scheme: dark)').matches;
  const isDark = pref.theme === 'DARK' || isSystemDark;

  if (isDark) {
    document.body.setAttribute('data-system-theme', 'DARK');
  } else {
    document.body.removeAttribute('data-system-theme');
  }
  document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
}
